import React, { useState, useEffect, useRef } from"react";
import { useLocation, useNavigate } from"react-router-dom";
import useSkillGenieSession from"../hooks/useSkillGenieSession";
import API_BASE_URL from"../Config/config";
import FeatureGuard from"./access/FeatureGuard";
import TutorShell, { RailSection, RailStat, RailTip } from "./aiTutor/TutorShell";
import { useTutorLanguage, LanguagePicker } from "./aiTutor/languages";
import {
  TutorMessages,
  TutorComposer,
  TutorBadge,
  TutorEmptyState,
} from "./aiTutor/TutorChat";
import NexusUsageBadge from "../NextGenBuilder/components/NexusUsageBadge";
import useNexusLearnerBilling from "../NextGenBuilder/hooks/useNexusLearnerBilling";


const CodeReviewPage = ({ mode = "learner" }) => {
 const isTenantMode = mode === "tenant";

 if (isTenantMode) {
   return <CodeReviewPageContent mode={mode} />;
 }

 return (
   <FeatureGuard feature="TSG_AI">
     <CodeReviewPageContent mode={mode} />
   </FeatureGuard>
 );
};

const CodeReviewPageContent = ({ mode = "learner" }) => {
 const [input, setInput] = useState("");
 const [chat, setChat] = useState([]);
 const [loading, setLoading] = useState(false);
 const chatEndRef = useRef(null);
 const navigate = useNavigate();

 const isTenantMode = mode === "tenant";

 // language from navigation state OR saved session
 const location = useLocation();
 const { session } = useSkillGenieSession();
  const { langCode, langName, setLanguage } = useTutorLanguage({
    initialLanguage: location.state?.language || session?.language,
  });

 // TSG Nexus feature billing — inert outside /nextgen-builder/*.
 const nexus = useNexusLearnerBilling("AI_TUTOR", { subFeature: "CODE_REVIEW" });

 const handleAsk = async () => {
 if (!input.trim()) return;

 const user = JSON.parse(localStorage.getItem("user") ||"{}");
 // NextGen Builder accounts don't have `.userId`/`.user_id` — fall back to
 // the namespaced pseudo-identity written to localStorage by
 // NextGenAuthContext.jsx, same as ExplainPage.jsx already does.
 const userId = user?.userId || user?.user_id || localStorage.getItem("user_id") ||"UnknownUser";

 if (!userId || userId ==="UnknownUser") {
 alert("Please log in to use this feature.");
 navigate(isTenantMode ? "/nextgen-builder/login" : "/login");
 return;
 }

    const currentInput = input;
    setChat((prev) => [...prev, { role: "user", content: currentInput }]);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/tsg/code_review`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...nexus.headers() },
        body: JSON.stringify({
          userId,
          prompt: currentInput,
          chatHistory: chat,
          language: langCode,
          technology: "general",
          topic: "general",
        }),
      });

      const data = await res.json();

      if (res.status === 402 && nexus.handleQuota(data)) {
        setChat((prev) => [
          ...prev,
          { role: "assistant", content: "You've used all your free Code Review prompts for this month. Recharge to continue." },
        ]);
        return;
      }

      nexus.noteUsage(res);

      const reply = data.reply || "No reply.";
      setChat((prev) => [
        ...prev,
        { role: "assistant", content: reply },
      ]);
    } catch {
 setChat((prev) => [
 ...prev,
 { role:"assistant", content:"❌ Error reaching TSG." },
 ]);
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
    const container = chatEndRef.current?.parentElement;
    if (container) {
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
      if (isAtBottom || chat.length <= 2) {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [chat]);

  return (
    <TutorShell
      language={<LanguagePicker value={langCode} onChange={setLanguage} />}
      icon={<svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>}
      title="Code Review"
      subtitle="Paste a file or a function and get it reviewed for correctness, security and readability."
      onBack={() => navigate(-1)}
      headerExtra={nexus.isNexus ? <NexusUsageBadge feature="AI_TUTOR" subFeature="CODE_REVIEW" /> : null}
      badges={<TutorBadge label="Lang" value={langName} dot="#22C55E" />}
      rail={
        <>
          <RailSection title="Session">
            <RailStat label="Review language" value={langName} hint="Findings are written in this language." />
            <RailStat label="Snippets reviewed" value={String(Math.ceil(chat.length / 2))} />
          </RailSection>
          <RailSection title="What gets checked">
            <RailTip>Correctness and edge cases — off-by-one, null paths, unhandled rejections.</RailTip>
            <RailTip>Security — injection, unvalidated input, secrets and unguarded routes.</RailTip>
            <RailTip>Readability and structure — naming, duplication, function size.</RailTip>
          </RailSection>
          <RailSection title="Get a sharper review">
            <RailTip>Paste the whole function, not a fragment. Missing context produces guesses.</RailTip>
            <RailTip>Say what the code is meant to do — reviews against intent catch far more.</RailTip>
          </RailSection>
        </>
      }
      composer={
        <TutorComposer
          value={input}
          onChange={setInput}
          onSubmit={handleAsk}
          loading={loading}
          disabled={!input.trim()}
          multiline
          placeholder="Paste code or ask for review..."
          hint={[nexus.hint, "Enter to send · Shift + Enter for a new line"].filter(Boolean).join(" · ")}
        />
      }
    >
      <TutorMessages
        chat={chat}
        chatEndRef={chatEndRef}
        assistantLabel="TSG Reviewer"
        empty={
          <TutorEmptyState
            icon={<svg className="w-9 h-9 text-[#5B21B6]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>}
            title="Paste code to review"
            description="Drop in a function, a file, or a diff. You get findings grouped by severity with concrete fixes."
          >
            <div className="rounded-2xl border border-[#E9E7F3] bg-white px-5 py-4 text-left">
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#94A3B8] mb-2">Reviewed for</p>
              <div className="flex flex-wrap gap-2">
                {["Correctness", "Security", "Performance", "Readability", "Error handling", "Tests"].map((t) => (
                  <span key={t} className="px-3 py-1.5 rounded-xl bg-[#F5F2FF] border border-[#E9E7F3] text-[11px] font-bold text-[#5B21B6]">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </TutorEmptyState>
        }
      />
    </TutorShell>
  );
};

export default CodeReviewPage;



